import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import * as ribCotisationActions from '../../../webservices/rib/cotisation/ribCotisationActions'
import { Box, Link, Stack, Typography } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import ButtonDefault from '../../../../components/ButtonDefault'
import CheckboxInput from '../../../../components/form/CheckboxInput'
import { Edit } from '@mui/icons-material'
import { STEP1_CHECKBOX_NAME, STEP2_FORM_NAME } from '../services/souscriptionConstants'
import TextInput from '../../../../components/form/TextInput'
import { COTISATIONS, NATURE, REMBOURSEMENTS } from '../../../webservices/rib/ribConsts'
import IBAN from 'iban'
import IBANTextInput from '../../../../components/IBANTextInput'
import FormatUtils from '../../../../utils/FormatUtils'

type GarantieSouscriptionRibSectionProps = {
	editRib: boolean,
	setEditRib: Dispatch<SetStateAction<boolean>>
	values: any
	rib: any
	errors: any
	garanties?: Garantie[]
	mandatPrelevement?: boolean
}

const isPrelevementSurSalaire = (rib: any) => rib && rib.nature !== NATURE.VIREMENT

const GarantieSouscriptionRibSection: React.FC<GarantieSouscriptionRibSectionProps> = (
	{
		// @ts-ignore
		downloadMandatSepa,
		editRib,
		setEditRib,
		values,
		rib,
		errors,
		garanties,
		mandatPrelevement
	}
) => {
	const intl = useIntl()

	const selectedOptionModePaie = useMemo(() => {
		const selectedOptionKey = values[STEP1_CHECKBOX_NAME] && values[STEP1_CHECKBOX_NAME][0]?.idGarantie[0]
		const selectedOption = garanties ? garanties.find((garantie: Garantie) => garantie.key === selectedOptionKey) : undefined

		return selectedOption && selectedOption.modePaie
	}, [values, garanties])

	useEffect(() => {
		if (selectedOptionModePaie === 2 && !rib) {
			setEditRib(true)
		}
	}, [selectedOptionModePaie, setEditRib, rib])

	const getLibellemandatSepa = (iban: string) => {
		if (isPrelevementSurSalaire(rib)) {
			return intl.formatMessage({ id: 'souscription.input.libelleMandatSepa.prelevementSalaire' })
		}

		if (mandatPrelevement && !editRib && rib) {
			iban = rib?.clefIban + rib?.bban
		}

		if (IBAN.isValid(iban)) {
			return intl.formatMessage({ id: mandatPrelevement ? 'mandatPrelevement.validation' : 'souscription.input.libelleMandatSepa.modifyCoord' }, {
				link: (
					<Link
						component="button"
						color="secondary"
						variant="body2"
						sx={{ verticalAlign: 'initial', fontWeight: 'bold', textDecoration: 'none' }}
						onClick={() => downloadMandatSepa(iban || (rib && rib?.iban), mandatPrelevement || false)}
					>
						<FormattedMessage id="souscription.input.libelleMandatSepa.sepa" />
					</Link>
				)
			})
		}

		return intl.formatMessage({ id: mandatPrelevement ? 'mandatPrelevement.default' : 'souscription.input.libelleMandatSepa.default' })
	}

	const getIntro = () => {
		if (mandatPrelevement) {
			return <FormattedMessage id="mandatPrelevement.texte" />
		}

		if (editRib) {
			return 'Merci de saisir ci-dessous votre RIB pour le prélèvement de vos cotisations :'
		}
		const ribType = rib?.type
		if (isPrelevementSurSalaire(rib)) {
			return (
				<span>Votre mode de paiement pour la souscription d'option est le suivant : <b>Prélèvement sur salaire</b></span>
			)
		}
		if (ribType === REMBOURSEMENTS) {
			return 'Coordonnées bancaires actuellement en notre possession pour le remboursement de vos soins de santé :'
		}
		if (ribType === COTISATIONS) {
			return 'Les coordonnées bancaires associées à vos cotisations sont les suivantes :'
		}
	}

	return (
		<Stack direction="column" spacing="24px">
			<Typography variant="body2">
				{getIntro()}
			</Typography>
			{!isPrelevementSurSalaire(rib) &&
				<>
					<Stack direction="column" spacing="24px">
						{!editRib ?
							<TextInput
								maxWidth="unset"
								name={"FakeField"}
								label={intl.formatMessage({ id: 'souscription.input.iban' })}
								disabled
								defaultValue={FormatUtils.formatIban(values[STEP2_FORM_NAME.IBAN], true)}
							/>
							:
							<IBANTextInput
								maxWidth="unset"
								name={STEP2_FORM_NAME.IBAN}
								label={intl.formatMessage({ id: 'souscription.input.iban' })}
								disabled={!editRib}
							/>
						}
						<TextInput
							maxWidth="unset"
							name={STEP2_FORM_NAME.BANK}
							label={intl.formatMessage({ id: 'souscription.input.bank' })}
							disabled={!editRib}
							maxLength={30}
						/>
						<TextInput
							maxWidth="unset"
							name={STEP2_FORM_NAME.ACCOUNT_HOLDER}
							label={intl.formatMessage({ id: 'souscription.input.titulaire' })}
							disabled={!editRib}
							maxLength={30}
						/>
					</Stack>
					{!editRib && (
						<Stack direction="column" spacing="12px">
							<Typography variant="body2">
								<FormattedMessage id="souscription.mode.editRib" />
							</Typography>
							<Box width="100%" display="flex" justifyContent="center">
								<ButtonDefault variant="text" color="secondary" onClick={() => setEditRib(true)} startIcon={<Edit />}>
									<FormattedMessage id="global.button.modify" />
								</ButtonDefault>
							</Box>
						</Stack>
					)}
				</>
			}
			<CheckboxInput
				name={STEP2_FORM_NAME.ACCEPT_SEPA}
				data={[
					{
						label: getLibellemandatSepa(values[STEP2_FORM_NAME.IBAN]),
						value: 'acceptMandatSepa'
					}
				]}
				error={errors && errors[STEP2_FORM_NAME.ACCEPT_SEPA]}
			/>
		</Stack>
	)
}

const mapStateToProps = () => ({})

const mappedActions = {
	downloadMandatSepa: ribCotisationActions.downloadMandatSepaOuPrelevement
}

export default compose(
	connect(mapStateToProps, mappedActions)(GarantieSouscriptionRibSection))
